import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from './ui/card';
import {
  Bell,
  CreditCard,
  Users,
  TrendingUp,
  Calendar,
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  Clock,
  CheckCircle,
  AlertCircle,
  Gift,
  Target,
  Camera,
  FileText,
  Upload,
  ChevronLeft,
  ChevronRight,
  Image,
  File,
  Send,
  X,
  Check,
  Loader,
  Plus,
  Paperclip,
  Download,
  Eye,
  Share,
  LogOut
} from 'lucide-react';

const EnhancedMemberDashboard = ({ user, userGroups, notifications, onNavigate, onJoinWithPermanentCode, onJoinGroup, onJoinWithCode, onSendMessage, onUploadFile, onLogout }) => {
  const [memberStats, setMemberStats] = useState({
    totalContributions: 0,
    totalGroups: userGroups?.length || 0,
    pendingPayments: 0,
    nextPayout: null,
    unreadNotifications: 0
  });

  const [currentScreen, setCurrentScreen] = useState(0);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState(''); // 'success', 'error', 'info'

  const fileInputRef = useRef(null);
  const cameraRef = useRef(null);
  const videoRef = useRef(null);

  const screens = [
    { id: 'overview', title: 'Aperçu', icon: Target },
    { id: 'groups', title: 'Mes Groupes', icon: Users },
    { id: 'payments', title: 'Paiements', icon: CreditCard },
    { id: 'wallet', title: 'Portefeuille', icon: Wallet }
  ];

  useEffect(() => {
    calculateMemberStats();
  }, [userGroups, notifications]);

  useEffect(() => {
    if (feedbackMessage) {
      const timer = setTimeout(() => {
        setFeedbackMessage('');
        setFeedbackType('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [feedbackMessage]);

  const calculateMemberStats = () => {
    if (!userGroups || userGroups.length === 0) return;

    let totalContributions = 0;
    let pendingPayments = 0;
    let nextPayout = null;

    userGroups.forEach(group => {
      totalContributions += group.contribution_amount * 3;
      const currentDate = new Date();
      const startDate = new Date(group.start_date);
      if (currentDate > startDate) {
        pendingPayments += 1;
      }
      
      if (!nextPayout || group.start_date < nextPayout) {
        nextPayout = new Date(Date.now() + 15 * 24 * 60 * 60 * 1000);
      }
    });

    const unreadNotifications = notifications?.filter(n => !n.is_read).length || 0;

    setMemberStats({
      totalContributions,
      totalGroups: userGroups.length,
      pendingPayments,
      nextPayout,
      unreadNotifications
    });
  };

  const showFeedback = (message, type = 'info') => {
    setFeedbackMessage(message);
    setFeedbackType(type);
  };

  const handleNavigation = (direction) => {
    if (direction === 'next' && currentScreen < screens.length - 1) {
      setCurrentScreen(currentScreen + 1);
      showFeedback('Navigation vers la section suivante', 'info');
    } else if (direction === 'prev' && currentScreen > 0) {
      setCurrentScreen(currentScreen - 1);
      showFeedback('Navigation vers la section précédente', 'info');
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' },
        audio: false 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      showFeedback('Caméra activée - Positionnez votre sujet', 'success');
    } catch (error) {
      console.error('Error accessing camera:', error);
      showFeedback('Impossible d\'accéder à la caméra. Vérifiez les permissions.', 'error');
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && cameraRef.current) {
      const context = cameraRef.current.getContext('2d');
      const { videoWidth, videoHeight } = videoRef.current;
      
      cameraRef.current.width = videoWidth;
      cameraRef.current.height = videoHeight;
      
      context.drawImage(videoRef.current, 0, 0);
      const imageData = cameraRef.current.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      
      // Stop camera stream
      const stream = videoRef.current.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      showFeedback('Photo capturée avec succès!', 'success');
    }
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (file.size > maxSize) {
        showFeedback('Fichier trop volumineux. Taille maximale: 10MB', 'error');
        return;
      }

      uploadFile(file);
    }
  };

  const uploadFile = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);
    showFeedback('Téléchargement en cours...', 'info');

    try {
      // Simulate upload progress
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        setUploadProgress(i);
      }

      // Call the onUploadFile callback if provided
      if (onUploadFile) {
        await onUploadFile(file);
      }

      showFeedback('Fichier téléchargé avec succès!', 'success');
    } catch (error) {
      console.error('Upload error:', error);
      showFeedback('Erreur lors du téléchargement. Réessayez.', 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setShowFileUploadModal(false);
    }
  };

  const sendCapturedPhoto = async () => {
    if (capturedImage && onSendMessage) {
      try {
        await onSendMessage({
          type: 'image',
          content: capturedImage,
          timestamp: new Date().toISOString()
        });
        setCapturedImage(null);
        setShowCameraModal(false);
        showFeedback('Photo envoyée avec succès!', 'success');
      } catch (error) {
        showFeedback('Erreur lors de l\'envoi. Réessayez.', 'error');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'Non planifié';
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(date));
  };

  const renderScreen = () => {
    const screen = screens[currentScreen];
    
    switch (screen.id) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-300 text-sm">Contributions</p>
                      <p className="text-xl font-bold">{formatCurrency(memberStats.totalContributions)}</p>
                    </div>
                    <TrendingUp className="w-6 h-6 text-green-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-300 text-sm">Groupes</p>
                      <p className="text-xl font-bold">{memberStats.totalGroups}</p>
                    </div>
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardHeader>
                <CardTitle>Actions Rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <button
                  onClick={() => onNavigate?.('payments')}
                  className="w-full flex items-center space-x-3 p-3 bg-green-600/20 hover:bg-green-600/30 rounded-lg transition-all"
                  aria-label="Effectuer un paiement"
                >
                  <CreditCard className="w-5 h-5 text-green-400" />
                  <span className="text-green-300">Effectuer un Paiement</span>
                </button>
                
                <button
                  onClick={() => setShowCameraModal(true)}
                  className="w-full flex items-center space-x-3 p-3 bg-blue-600/20 hover:bg-blue-600/30 rounded-lg transition-all"
                  aria-label="Prendre une photo"
                >
                  <Camera className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-300">Prendre une Photo</span>
                </button>
                
                <button
                  onClick={() => setShowFileUploadModal(true)}
                  className="w-full flex items-center space-x-3 p-3 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg transition-all"
                  aria-label="Ajouter un fichier"
                >
                  <FileText className="w-5 h-5 text-purple-400" />
                  <span className="text-purple-300">Ajouter un Fichier</span>
                </button>
              </CardContent>
            </Card>
          </div>
        );

      case 'groups':
        return (
          <div className="space-y-4">
            {userGroups && userGroups.length > 0 ? (
              userGroups.map((group) => (
                <Card key={group.id} className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">
                            {group.name.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-white">{group.name}</h3>
                          <p className="text-sm text-slate-300">
                            {group.members?.length || 0} membres
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium">{formatCurrency(group.contribution_amount)}</p>
                        <p className="text-sm text-slate-400">Mensuel</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Aucun groupe rejoint</h3>
                  <p className="text-slate-300 mb-6">Rejoignez votre premier groupe pour commencer</p>
                  
                  {/* Options de rejoindre un groupe */}
                  <div className="space-y-3 max-w-md mx-auto">
                    {/* Code Permanent - Option principal */}
                    <button
                      onClick={onJoinWithPermanentCode}
                      className="w-full flex items-center justify-center space-x-3 px-6 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 rounded-xl text-white font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg"
                      aria-label="Rejoindre avec code permanent"
                    >
                      <span className="text-xl">🔑</span>
                      <span>Code Permanent</span>
                      <span className="text-xs bg-white/20 px-2 py-1 rounded-full">INSTANTANÉ</span>
                    </button>
                    
                    {/* Demande d'adhésion */}
                    <button
                      onClick={onJoinGroup}
                      className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-white font-medium transition-colors"
                      aria-label="Faire une demande d'adhésion"
                    >
                      <span className="text-lg">🤝</span>
                      <span>Demande d'Adhésion</span>
                    </button>
                    
                    {/* Code temporaire */}
                    <button
                      onClick={onJoinWithCode}
                      className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-green-600 hover:bg-green-700 rounded-xl text-white font-medium transition-colors"
                      aria-label="Rejoindre avec code temporaire"
                    >
                      <span className="text-lg">✅</span>
                      <span>Code Temporaire</span>
                    </button>
                    
                    {/* Rechercher des groupes */}
                    <button
                      onClick={() => onNavigate?.('search')}
                      className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-xl text-white font-medium transition-colors"
                      aria-label="Rechercher des groupes"
                    >
                      <span className="text-lg">🔍</span>
                      <span>Explorer les Groupes</span>
                    </button>
                  </div>
                  
                  {/* Message d'aide */}
                  <div className="mt-6 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                    <p className="text-blue-200 text-sm">
                      💡 <strong>Conseil :</strong> Si vous avez un code permanent (8 caractères), utilisez l'option "Code Permanent" pour un accès immédiat !
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-4">
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardHeader>
                <CardTitle>Statut des Paiements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-300">Paiements à jour</span>
                    </div>
                    <span className="text-sm text-green-300">Jan 2025</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardHeader>
                <CardTitle>Méthodes de Paiement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <button
                  onClick={() => onNavigate?.('mpesa')}
                  className="w-full flex items-center space-x-3 p-3 bg-green-600/20 hover:bg-green-600/30 rounded-lg transition-all"
                  aria-label="Payer avec M-Pesa"
                >
                  <CreditCard className="w-5 h-5 text-green-400" />
                  <span className="text-green-300">M-Pesa</span>
                </button>
                
                <button
                  onClick={() => onNavigate?.('crypto')}
                  className="w-full flex items-center space-x-3 p-3 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg transition-all"
                  aria-label="Payer avec crypto-monnaie"
                >
                  <Wallet className="w-5 h-5 text-purple-400" />
                  <span className="text-purple-300">Crypto-monnaie</span>
                </button>
              </CardContent>
            </Card>
          </div>
        );

      case 'wallet':
        return (
          <div className="space-y-4">
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardContent className="p-6 text-center">
                <Wallet className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Portefeuille Crypto</h3>
                <p className="text-slate-300 mb-6">Gérez vos actifs numériques</p>
                <button
                  onClick={() => onNavigate?.('crypto')}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
                  aria-label="Ouvrir le portefeuille crypto"
                >
                  Ouvrir Portefeuille
                </button>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Karibu, {user?.username}! 👋</h1>
            <p className="text-purple-100">
              {screens[currentScreen].title}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onNavigate?.('notifications')}
              className="relative p-3 bg-white/20 rounded-full hover:bg-white/30 transition-all"
              aria-label="Voir les notifications"
            >
              <Bell className="w-5 h-5" />
              {memberStats.unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {memberStats.unreadNotifications}
                </span>
              )}
            </button>
            
            {/* Bouton de déconnexion pour membres */}
            <button
              onClick={onLogout || (() => {
                // Fallback si onLogout n'est pas fourni
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('groups');
                sessionStorage.clear();
                
                alert('✅ Vous êtes déconnecté·e avec succès');
                window.location.reload();
              })}
              className="p-3 bg-red-500/20 rounded-full hover:bg-red-500/30 transition-all"
              title="Déconnexion"
              aria-label="Se déconnecter de l'application"
            >
              <LogOut className="w-5 h-5 text-red-400" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 mt-6 bg-white/10 rounded-lg p-1">
          {screens.map((screen, index) => {
            const IconComponent = screen.icon;
            return (
              <button
                key={screen.id}
                onClick={() => setCurrentScreen(index)}
                className={`flex-1 flex flex-col items-center space-y-1 py-2 px-2 rounded-md transition-all ${
                  currentScreen === index 
                    ? 'bg-white/20 text-white' 
                    : 'text-purple-200 hover:text-white hover:bg-white/10'
                }`}
                aria-label={`Aller à ${screen.title}`}
              >
                <IconComponent className="w-5 h-5" />
                <span className="text-xs">{screen.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-6">
        {renderScreen()}
      </div>

      {/* Navigation Controls */}
      <div className="fixed bottom-6 left-4 right-4 flex justify-between items-center">
        <button
          onClick={() => handleNavigation('prev')}
          disabled={currentScreen === 0}
          className={`flex items-center space-x-2 px-4 py-3 rounded-full shadow-lg transition-all ${
            currentScreen === 0
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          }`}
          aria-label="Section précédente"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Précédent</span>
        </button>

        <div className="flex space-x-2">
          {screens.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                currentScreen === index ? 'bg-white' : 'bg-white/40'
              }`}
            />
          ))}
        </div>

        <button
          onClick={() => handleNavigation('next')}
          disabled={currentScreen === screens.length - 1}
          className={`flex items-center space-x-2 px-4 py-3 rounded-full shadow-lg transition-all ${
            currentScreen === screens.length - 1
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          }`}
          aria-label="Section suivante"
        >
          <span>Suivant</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Camera Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg max-w-md w-full overflow-hidden">
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Prendre une Photo</h3>
                <button
                  onClick={() => {
                    setShowCameraModal(false);
                    setCapturedImage(null);
                    // Stop camera stream if active
                    if (videoRef.current?.srcObject) {
                      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
                    }
                  }}
                  className="p-2 hover:bg-slate-700 rounded-full text-white"
                  aria-label="Fermer la caméra"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              {!capturedImage ? (
                <div className="space-y-4">
                  <div className="aspect-square bg-slate-700 rounded-lg overflow-hidden">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={startCamera}
                      className="flex-1 flex items-center justify-center space-x-2 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      aria-label="Démarrer la caméra"
                    >
                      <Camera className="w-5 h-5" />
                      <span>Démarrer Caméra</span>
                    </button>
                    <button
                      onClick={capturePhoto}
                      disabled={!videoRef.current?.srcObject}
                      className="flex-1 flex items-center justify-center space-x-2 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                      aria-label="Capturer la photo"
                    >
                      <Camera className="w-5 h-5" />
                      <span>Capturer</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="aspect-square bg-slate-700 rounded-lg overflow-hidden">
                    <img
                      src={capturedImage}
                      alt="Photo capturée"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setCapturedImage(null)}
                      className="flex-1 flex items-center justify-center space-x-2 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                      aria-label="Reprendre la photo"
                    >
                      <Camera className="w-5 h-5" />
                      <span>Reprendre</span>
                    </button>
                    <button
                      onClick={sendCapturedPhoto}
                      className="flex-1 flex items-center justify-center space-x-2 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                      aria-label="Envoyer la photo"
                    >
                      <Send className="w-5 h-5" />
                      <span>Envoyer</span>
                    </button>
                  </div>
                </div>
              )}
              <canvas ref={cameraRef} className="hidden" />
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      {showFileUploadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg max-w-md w-full">
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Ajouter un Fichier</h3>
                <button
                  onClick={() => setShowFileUploadModal(false)}
                  className="p-2 hover:bg-slate-700 rounded-full text-white"
                  aria-label="Fermer l'upload"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto">
                  <FileText className="w-8 h-8 text-purple-400" />
                </div>
                <div>
                  <h4 className="text-white font-medium mb-2">Sélectionner un fichier</h4>
                  <p className="text-slate-400 text-sm mb-4">
                    Photos, PDF, documents (max 10MB)
                  </p>
                </div>
                
                {isUploading && (
                  <div className="w-full bg-slate-700 rounded-full h-2 mb-4">
                    <div 
                      className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="flex flex-col items-center space-y-2 p-4 bg-blue-600/20 hover:bg-blue-600/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all"
                    aria-label="Choisir un fichier"
                  >
                    <Image className="w-6 h-6 text-blue-400" />
                    <span className="text-blue-300 text-sm">Images</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.pdf,.doc,.docx,.txt';
                      input.onchange = handleFileSelect;
                      input.click();
                    }}
                    disabled={isUploading}
                    className="flex flex-col items-center space-y-2 p-4 bg-green-600/20 hover:bg-green-600/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all"
                    aria-label="Choisir un document"
                  >
                    <File className="w-6 h-6 text-green-400" />
                    <span className="text-green-300 text-sm">Documents</span>
                  </button>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Toast */}
      {feedbackMessage && (
        <div 
          className={`fixed top-4 left-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-3 ${
            feedbackType === 'success' ? 'bg-green-600 text-white' :
            feedbackType === 'error' ? 'bg-red-600 text-white' :
            'bg-blue-600 text-white'
          }`}
          role="alert"
          aria-live="polite"
        >
          {feedbackType === 'success' && <CheckCircle className="w-5 h-5" />}
          {feedbackType === 'error' && <AlertCircle className="w-5 h-5" />}
          {feedbackType === 'info' && <Clock className="w-5 h-5" />}
          <span className="flex-1">{feedbackMessage}</span>
        </div>
      )}

      {/* Bouton flottant pour rejoindre plus de groupes */}
      {userGroups && userGroups.length > 0 && (
        <div className="fixed bottom-20 right-6 z-40">
          <div className="relative group">
            {/* Bouton principal */}
            <button
              onClick={onJoinWithPermanentCode}
              className="w-14 h-14 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 rounded-full shadow-lg flex items-center justify-center text-white transition-all duration-200 transform hover:scale-110 group-hover:shadow-xl"
              aria-label="Rejoindre plus de groupes"
              title="Rejoindre avec Code Permanent"
            >
              <span className="text-xl">🔑</span>
            </button>
            
            {/* Tooltip */}
            <div className="absolute bottom-16 right-0 bg-black/80 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              Code Permanent ⚡
              <div className="absolute top-full right-3 border-4 border-transparent border-t-black/80"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedMemberDashboard;